import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Store, Plus, Edit2, Trash2, Globe, Calendar, Hash, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatStoreName } from '@/lib/storeUtils';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';

const API_URL = getApiBaseUrl();

// Common IANA timezones
const TIMEZONES = [
  // Australia
  { value: 'Australia/Sydney', label: 'Sydney (UTC+11/+10 DST)', region: 'Australia' },
  { value: 'Australia/Melbourne', label: 'Melbourne (UTC+11/+10 DST)', region: 'Australia' },
  { value: 'Australia/Perth', label: 'Perth (UTC+8)', region: 'Australia' },
  { value: 'Australia/Brisbane', label: 'Brisbane (UTC+10)', region: 'Australia' },
  { value: 'Australia/Adelaide', label: 'Adelaide (UTC+10:30/+9:30 DST)', region: 'Australia' },
  
  // Asia
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)', region: 'Asia' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (UTC+5:30)', region: 'Asia' },
  
  // Europe
  { value: 'Europe/London', label: 'London (UTC+0/+1 DST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1/+2 DST)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1/+2 DST)', region: 'Europe' },
  
  // Americas
  { value: 'America/New_York', label: 'New York (UTC-5/-4 DST)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/-7 DST)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Chicago (UTC-6/-5 DST)', region: 'Americas' },
];

const StoresPage = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    store_id: '',
    name: '',
    prefix: '',
    location: '',
    timezone: 'Asia/Singapore',
    epoch_start_date: new Date().toISOString().split('T')[0],
    address: '',
    phone: '',
    email: '',
    active: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (store = null) => {
    if (store) {
      setEditingStore(store);
      setFormData({
        store_id: store.store_id,
        name: store.name,
        prefix: store.prefix,
        location: store.location,
        timezone: store.timezone,
        epoch_start_date: store.epoch_start_date,
        address: store.address || '',
        phone: store.phone || '',
        email: store.email || '',
        active: store.active !== false
      });
    } else {
      setEditingStore(null);
      setFormData({
        store_id: '',
        name: '',
        prefix: '',
        location: '',
        timezone: 'Asia/Singapore',
        epoch_start_date: new Date().toISOString().split('T')[0],
        address: '',
        phone: '',
        email: '',
        active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingStore(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      
      if (editingStore) {
        // Update existing store
        await axios.put(
          `${API_URL}/api/stores/${editingStore.store_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Store updated successfully');
      } else {
        // Create new store
        await axios.post(
          `${API_URL}/api/stores`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Store created successfully');
      }

      handleCloseModal();
      fetchStores();
    } catch (error) {
      console.error('Error saving store:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save store';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('Are you sure you want to delete this store?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Store deleted successfully');
      fetchStores();
    } catch (error) {
      console.error('Error deleting store:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete store';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Store className="w-8 h-8" />
            Store Management
          </h1>
          <p className="text-slate-600 mt-1">
            Configure stores, timezones, and order number prefixes
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} size="lg">
          <Plus className="w-5 h-5 mr-2" />
          Add Store
        </Button>
      </div>

      {/* Stores Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Stores ({stores.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {stores.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Store className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>No stores configured yet</p>
              <Button onClick={() => handleOpenModal()} className="mt-4">
                Add Your First Store
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Store Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead>Epoch Start</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.map((store) => (
                  <TableRow key={store.store_id}>
                    <TableCell className="font-mono font-bold text-blue-600">
                      {store.prefix || 'N/A'}
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatStoreName(store.name)}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {store.location || 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Globe className="w-4 h-4" />
                        {store.timezone ? (store.timezone.includes('/') ? store.timezone.split('/')[1] : store.timezone) : 'Not set'}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {store.epoch_start_date || 'Not set'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          store.active !== false
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {store.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(store)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(store.store_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Store Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStore ? 'Edit Store' : 'Add New Store'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            {/* Store ID (only for new stores) */}
            {!editingStore && (
              <div>
                <Label htmlFor="store_id">
                  Store ID * <span className="text-xs text-slate-500">(e.g., store_sydney)</span>
                </Label>
                <Input
                  id="store_id"
                  required
                  value={formData.store_id}
                  onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                  placeholder="store_sydney"
                />
              </div>
            )}

            {/* Store Name */}
            <div>
              <Label htmlFor="name">Store Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Sydney Store"
              />
            </div>

            {/* Prefix */}
            <div>
              <Label htmlFor="prefix">
                Order Prefix * <span className="text-xs text-slate-500">(2-3 characters)</span>
              </Label>
              <Input
                id="prefix"
                required
                maxLength={3}
                value={formData.prefix}
                onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                placeholder="SYD"
                className="font-mono"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Location */}
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Sydney, Australia"
                />
              </div>

              {/* Epoch Start Date */}
              <div>
                <Label htmlFor="epoch_start_date">Epoch Start Date *</Label>
                <Input
                  id="epoch_start_date"
                  type="date"
                  required
                  value={formData.epoch_start_date}
                  onChange={(e) => setFormData({ ...formData, epoch_start_date: e.target.value })}
                />
              </div>
            </div>

            {/* Timezone */}
            <div>
              <Label htmlFor="timezone">Timezone (IANA) *</Label>
              <Select
                value={formData.timezone}
                onValueChange={(value) => setFormData({ ...formData, timezone: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                DST (Daylight Saving Time) handled automatically
              </p>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-slate-700">Contact Information (Optional)</h3>
              
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+61 2 1234 5678"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="sydney@perfectattire.com"
                  />
                </div>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2 pt-4 border-t">
              <Checkbox
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
              <Label htmlFor="active" className="cursor-pointer">
                Store is active
              </Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingStore ? (
                  'Update Store'
                ) : (
                  'Create Store'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoresPage;
